import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCircle2 } from 'lucide-react';
import API from '../../../util/API';

const cx = (...arr) => arr.filter(Boolean).join(' ');

export default function NotificationsPage() {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  const unreadCount = useMemo(
    () => notifications.filter((n) => !n?.isRead).length,
    [notifications]
  );

  const loadAll = async () => {
    setLoading(true);
    try {
      const res = await API.get('/notification/admin?limit=100');
      const list = Array.isArray(res?.data?.data) ? res.data.data : [];
      setNotifications(list);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
  }, []);

  const markRead = async (id) => {
    if (!id) return;
    const res = await API.patch(`/notification/admin/${id}/read`);
    const updated = res?.data?.data;
    if (!updated?._id) return;
    setNotifications((prev) =>
      prev.map((n) => (String(n._id) === String(updated._id) ? { ...n, ...updated } : n))
    );
  };

  const markAllRead = async () => {
    await API.patch('/notification/admin/read-all');
    setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })));
  };

  return (
    <div className="max-w-5xl mx-auto">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-xl bg-gray-100 flex items-center justify-center">
            <Bell className="w-5 h-5 text-gray-700" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Notifications</h1>
            <p className="text-sm text-gray-600">
              {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={loadAll}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            Refresh
          </button>
          <button
            type="button"
            onClick={markAllRead}
            disabled={unreadCount === 0}
            className={cx(
              'px-3 py-2 text-sm rounded-lg',
              unreadCount === 0
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            )}
          >
            Mark all as read
          </button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-6 text-sm text-gray-500">Loading...</div>
        ) : notifications.length === 0 ? (
          <div className="p-6 text-sm text-gray-500">No notifications found.</div>
        ) : (
          <div className="divide-y divide-gray-100">
            {notifications.map((n) => {
              const id = n?._id;
              const isRead = Boolean(n?.isRead);
              const title = n?.title || 'Notification';
              const message = n?.message;
              const time = n?.createdAt ? new Date(n.createdAt).toLocaleString() : '';

              return (
                <div
                  key={id}
                  className={cx('p-4 flex items-start gap-3', !isRead && 'bg-blue-50')}
                >
                  <div className="mt-0.5">
                    <CheckCircle2 className={cx('w-5 h-5', isRead ? 'text-gray-300' : 'text-blue-600')} />
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">{title}</p>
                        {message ? (
                          <p className="text-sm text-gray-700 mt-0.5 break-words">{message}</p>
                        ) : null}
                        {time ? <p className="text-xs text-gray-500 mt-1">{time}</p> : null}
                      </div>

                      <div className="flex items-center gap-2 shrink-0">
                        {n?.link ? (
                          <button
                            type="button"
                            onClick={() => {
                              if (!isRead) markRead(id);
                              navigate(n.link);
                            }}
                            className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg hover:bg-white"
                          >
                            Open
                          </button>
                        ) : null}

                        {!isRead ? (
                          <button
                            type="button"
                            onClick={() => markRead(id)}
                            className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                          >
                            Mark read
                          </button>
                        ) : (
                          <span className="text-xs text-gray-400">Read</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
